// FinnishSitesApp — Data center site candidates in Finland
// Shows municipalities with georeferenced zoning plans, substations, and DC-suitable parcels

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Map, { Source, Layer, Marker, Popup, NavigationControl } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './finnish.css';

const MAPBOX_TOKEN = import.meta.env?.VITE_MAPBOX_TOKEN || '';

// Finland center
const FINLAND_CENTER = { longitude: 25.0, latitude: 64.0, zoom: 5 };

// Seinäjoki site center (our validated site)
const SEINAJOKI_CENTER = { longitude: 22.8348, latitude: 62.7907, zoom: 13 };

// Map style toggle
const MAP_STYLES = {
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
  light: 'mapbox://styles/mapbox/light-v11',
};

// Seinäjoki substation
const SEINAJOKI_STATION = {
  name: 'SEINÄJOKI 110 kV',
  lat: 62.787,
  lon: 22.832,
  voltage: 110,
  capacity_mw: 200,
  free_mw: 200,
};

// VACANT PARCELS - Actual cadastral parcels from MML
// Filtered: industrial zoning + low population + few buildings + compact shape (not roads)
// Shape index < 5 ensures parcels are not elongated road reserves
const VACANT_PARCELS = [
  { 
    parcel_id: '74340100050025',
    area_ha: 5.75,
    dist_km: 1.75,
    buildings: 0,
    status: 'best',
    geometry: {"type":"Polygon","coordinates":[[[22.823313039,62.802661256],[22.822850037,62.802884723],[22.822298672,62.802898785],[22.820756482,62.803613625],[22.820843146,62.803810673],[22.819544393,62.8044157],[22.820424935,62.804719249],[22.821092477,62.804615977],[22.821509655,62.805076743],[22.822220095,62.804984153],[22.82250161,62.805110177],[22.827919432,62.802596166],[22.823313039,62.802661256]]]}
  },
  { 
    parcel_id: '74340100010088',
    area_ha: 4.65,
    dist_km: 1.54,
    buildings: 0,
    status: 'best',
    geometry: {"type":"Polygon","coordinates":[[[22.815614648,62.800586004],[22.819511491,62.800913909],[22.819557125,62.800213719],[22.819449171,62.799603356],[22.813212623,62.798790184],[22.813935758,62.800021553],[22.815614648,62.800586004]]]}
  },
  { 
    parcel_id: '74340100010087',
    area_ha: 4.56,
    dist_km: 1.56,
    buildings: 0,
    status: 'best',
    geometry: {"type":"Polygon","coordinates":[[[22.812382596,62.798073762],[22.812020663,62.802246504],[22.813583713,62.802191895],[22.815557842,62.80181156],[22.815504463,62.80081235],[22.813636868,62.800034986],[22.812950515,62.798929342],[22.812938832,62.797956811],[22.812382596,62.798073762]]]}
  },
  { 
    parcel_id: '74301300010002',
    area_ha: 3.58,
    dist_km: 1.38,
    buildings: 4,
    status: 'good',
    geometry: {"type":"Polygon","coordinates":[[[22.814906151,62.799011034],[22.818965166,62.799540274],[22.819450777,62.79879364],[22.819458232,62.797941344],[22.813458822,62.797862583],[22.813158172,62.798185121],[22.814958878,62.798128127],[22.814906151,62.799011034]]]}
  },
  { 
    parcel_id: '74340100270017',
    area_ha: 3.57,
    dist_km: 1.59,
    buildings: 0,
    status: 'best',
    geometry: {"type":"Polygon","coordinates":[[[22.81094384,62.798337246],[22.810347182,62.802197525],[22.812020663,62.802246504],[22.81238439,62.798053139],[22.81094384,62.798337246]]]}
  },
  { 
    parcel_id: '74340100010161',
    area_ha: 2.24,
    dist_km: 1.68,
    buildings: 0,
    status: 'good',
    geometry: {"type":"Polygon","coordinates":[[[22.818414407,62.800821614],[22.815614648,62.800586004],[22.8158428,62.800831624],[22.815911612,62.801778866],[22.818858054,62.801925477],[22.819331567,62.801436067],[22.819511491,62.800913909],[22.818414407,62.800821614]]]}
  },
  { 
    parcel_id: '74340100050864',
    area_ha: 2.18,
    dist_km: 1.53,
    buildings: 0,
    status: 'good',
    geometry: {"type":"Polygon","coordinates":[[[22.818652284,62.803968067],[22.819108544,62.80414235],[22.82643828,62.800711038],[22.825834973,62.800457275],[22.819118114,62.803609882],[22.819250134,62.803670219],[22.818652284,62.803968067]]]}
  },
];

// Other low-population areas (NOT industrially zoned - would need rezoning)
const POTENTIAL_AREAS = [
  { 
    grid_id: '1kmN6965E0291', 
    population: 1, 
    buildings: 1,
    area_ha: 100, 
    dist_km: 4.48,
    status: 'needs_rezoning',
    zoning: 'Agricultural/Forest',
    geometry: [[22.925529337,62.75615106],[22.924292077,62.765103953],[22.904756905,62.764535384],[22.906000056,62.755582708],[22.925529337,62.75615106]]
  },
  { 
    grid_id: '1kmN6971E0282', 
    population: 2, 
    buildings: 0,
    area_ha: 100, 
    dist_km: 4.99,
    status: 'needs_rezoning',
    zoning: 'Agricultural/Forest',
    geometry: [[22.74204211,62.804643358],[22.740746542,62.8135941],[22.721183823,62.812999777],[22.722485298,62.804049263],[22.74204211,62.804643358]]
  },
  { 
    grid_id: '1kmN6964E0291', 
    population: 3, 
    buildings: 0,
    area_ha: 100, 
    dist_km: 5.15,
    status: 'needs_rezoning',
    zoning: 'Mine/Construction site',
    geometry: [[22.926765748,62.747198141],[22.925529337,62.75615106],[22.906000056,62.755582708],[22.907242353,62.746630007],[22.926765748,62.747198141]]
  },
  { 
    grid_id: '1kmN6972E0283', 
    population: 7, 
    buildings: 0,
    area_ha: 100, 
    dist_km: 4.75,
    status: 'needs_rezoning',
    zoning: 'Agricultural/Forest',
    geometry: [[22.760310152,62.814185707],[22.759019603,62.823136648],[22.739450082,62.822544815],[22.740746542,62.8135941],[22.760310152,62.814185707]]
  },
];

// DC-suitable parcels from zoning analysis (for reference - may have buildings)
const DC_PARCELS = [
  { kortteli: '4738', land_use: 'T', land_use_en: 'Industrial area', area_ha: 9.5, lat: 62.7898, lon: 22.8388, dist_km: 0.5 },
  { kortteli: '1400', land_use: 'KT', land_use_en: 'Commercial/office', area_ha: 7.6, lat: 62.7909, lon: 22.8427, dist_km: 0.7 },
  { kortteli: '3000', land_use: 'T', land_use_en: 'Industrial area', area_ha: 2.3, lat: 62.7920, lon: 22.8363, dist_km: 0.6 },
  { kortteli: '1500', land_use: 'KT', land_use_en: 'Commercial/office', area_ha: 8.3, lat: 62.7867, lon: 22.8434, dist_km: 0.6 },
  { kortteli: '4013', land_use: 'T', land_use_en: 'Industrial area', area_ha: 3.8, lat: 62.7934, lon: 22.8364, dist_km: 0.7 },
];

// Municipality boundary (approximate)
const SEINAJOKI_BOUNDARY = {
  type: 'Feature',
  properties: { name: 'Seinäjoki' },
  geometry: {
    type: 'Polygon',
    coordinates: [[
      [22.75, 62.75],
      [22.95, 62.75],
      [22.95, 62.85],
      [22.75, 62.85],
      [22.75, 62.75],
    ]],
  },
};

// Convert vacant parcels to GeoJSON (actual cadastral boundaries)
const vacantParcelsGeoJSON = {
  type: 'FeatureCollection',
  features: VACANT_PARCELS.map((p, i) => ({
    type: 'Feature',
    id: i,
    properties: {
      parcel_id: p.parcel_id,
      area_ha: p.area_ha,
      dist_km: p.dist_km,
      buildings: p.buildings,
      status: p.status,
    },
    geometry: p.geometry,
  })),
};


// Convert potential areas to GeoJSON (need rezoning)
const potentialAreasGeoJSON = {
  type: 'FeatureCollection',
  features: POTENTIAL_AREAS.map((v, i) => ({
    type: 'Feature',
    id: i + 100,
    properties: {
      grid_id: v.grid_id,
      population: v.population,
      buildings: v.buildings,
      area_ha: v.area_ha,
      dist_km: v.dist_km,
      status: v.status,
      zoning: v.zoning,
    },
    geometry: {
      type: 'Polygon',
      coordinates: [v.geometry],
    },
  })),
};

// Convert parcels to GeoJSON (zoning reference)
const parcelsGeoJSON = {
  type: 'FeatureCollection',
  features: DC_PARCELS.map((p, i) => ({
    type: 'Feature',
    id: i,
    properties: {
      kortteli: p.kortteli,
      land_use: p.land_use,
      land_use_en: p.land_use_en,
      area_ha: p.area_ha,
      dist_km: p.dist_km,
    },
    geometry: {
      type: 'Point',
      coordinates: [p.lon, p.lat],
    },
  })),
};

// Station as GeoJSON
const stationGeoJSON = {
  type: 'FeatureCollection',
  features: [{
    type: 'Feature',
    properties: {
      name: SEINAJOKI_STATION.name,
      voltage: SEINAJOKI_STATION.voltage,
      capacity_mw: SEINAJOKI_STATION.capacity_mw,
      free_mw: SEINAJOKI_STATION.free_mw,
    },
    geometry: {
      type: 'Point',
      coordinates: [SEINAJOKI_STATION.lon, SEINAJOKI_STATION.lat],
    },
  }],
};

// 6km radius circle around station
const stationRadiusGeoJSON = {
  type: 'Feature',
  properties: {},
  geometry: {
    type: 'Polygon',
    coordinates: [
      Array.from({ length: 64 }, (_, i) => {
        const angle = (i / 64) * 2 * Math.PI;
        const radiusKm = 6;
        const lat = SEINAJOKI_STATION.lat + (radiusKm / 111) * Math.cos(angle);
        const lon = SEINAJOKI_STATION.lon + (radiusKm / (111 * Math.cos(SEINAJOKI_STATION.lat * Math.PI / 180))) * Math.sin(angle);
        return [lon, lat];
      }),
    ],
  },
};

export default function FinnishSitesApp() {
  const mapRef = useRef(null);
  const [viewState, setViewState] = useState(SEINAJOKI_CENTER);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [hoveredParcel, setHoveredParcel] = useState(null);
  const [mapStyle, setMapStyle] = useState('satellite');
  const [copyMessage, setCopyMessage] = useState(null);

  const copyToClipboard = useCallback((lat, lon) => {
    const coordStr = `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
    navigator.clipboard.writeText(coordStr);
    setCopyMessage(`Copied: ${coordStr}`);
    setTimeout(() => setCopyMessage(null), 5000);
  }, []);

  const totalArea = DC_PARCELS.reduce((sum, p) => sum + p.area_ha, 0);
  const industrialArea = DC_PARCELS.filter(p => p.land_use === 'T').reduce((sum, p) => sum + p.area_ha, 0);
  const commercialArea = DC_PARCELS.filter(p => ['K', 'KT', 'KTY'].includes(p.land_use)).reduce((sum, p) => sum + p.area_ha, 0);

  const handleMapClick = useCallback((e) => {
    const features = e.features || [];
    const parcel = features.find(f => f.layer?.id === 'parcels-layer');
    if (parcel) {
      setSelectedParcel(parcel.properties);
    } else {
      setSelectedParcel(null);
    }
    // Also check for vacant parcel clicks
    const vacantParcel = features.find(f => f.layer?.id === 'vacant-parcels-fill');
    if (vacantParcel) {
      const coords = e.lngLat;
      copyToClipboard(coords.lat, coords.lng);
    }
  }, [copyToClipboard]);

  const handleMouseEnter = useCallback((e) => {
    const features = e.features || [];
    const parcel = features.find(f => f.layer?.id === 'parcels-layer');
    if (parcel) {
      setHoveredParcel(parcel.properties);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredParcel(null);
  }, []);

  return (
    <div className="finnish-sites-app">
      {/* Header */}
      <header className="finnish-header">
        <div className="finnish-logo">SEIDR</div>
        <h1>Finnish Data Center Sites</h1>
        <div className="finnish-nav">
          <a href="/app">← Back to App</a>
        </div>
      </header>

      <div className="finnish-content">
        {/* Sidebar */}
        <aside className="finnish-sidebar">
          {/* Info banner */}
          <div className="finnish-info">
            <span className="finnish-info-icon">🏭</span>
            <div>
              <strong>Cadastral Parcels from MML</strong>
              <p>Actual property boundaries cross-referenced with CORINE industrial zoning and low population areas.</p>
            </div>
          </div>

          <div className="finnish-site-card">
            <h2>Vacant Industrial Parcels</h2>
            <div className="finnish-site-status finnish-status-verified">✓ MML Cadastre</div>
            
            <div className="finnish-stats">
              <div className="finnish-stat">
                <span className="finnish-stat-value">{VACANT_PARCELS.reduce((s, p) => s + p.area_ha, 0).toFixed(1)} ha</span>
                <span className="finnish-stat-label">Total area</span>
              </div>
              <div className="finnish-stat">
                <span className="finnish-stat-value">{VACANT_PARCELS.length}</span>
                <span className="finnish-stat-label">Parcels</span>
              </div>
              <div className="finnish-stat">
                <span className="finnish-stat-value">{Math.min(...VACANT_PARCELS.map(p => p.dist_km)).toFixed(2)} km</span>
                <span className="finnish-stat-label">Nearest</span>
              </div>
            </div>

            <div className="finnish-breakdown">
              <h3>Screening Criteria</h3>
              <div className="finnish-criteria-row">
                <span className="finnish-check">✓</span>
                <span>Industrial zoning (CORINE)</span>
              </div>
              <div className="finnish-criteria-row">
                <span className="finnish-check">✓</span>
                <span>Low population area</span>
              </div>
              <div className="finnish-criteria-row">
                <span className="finnish-check">✓</span>
                <span>Few/no buildings</span>
              </div>
              <div className="finnish-criteria-row">
                <span className="finnish-check">✓</span>
                <span>Within 6km of 110kV station</span>
              </div>
            </div>

            <div className="finnish-breakdown">
              <h3>Potential Sites (Need Rezoning)</h3>
              <p className="finnish-note">{POTENTIAL_AREAS.length} additional areas with low population but agricultural/forest zoning</p>
            </div>

            <div className="finnish-grid-info">
              <h3>Grid Connection</h3>
              <div className="finnish-grid-row">
                <span>Station</span>
                <span>{SEINAJOKI_STATION.name}</span>
              </div>
              <div className="finnish-grid-row">
                <span>Voltage</span>
                <span>{SEINAJOKI_STATION.voltage} kV</span>
              </div>
              <div className="finnish-grid-row">
                <span>Available Capacity</span>
                <span className="finnish-capacity-good">{SEINAJOKI_STATION.free_mw} MW</span>
              </div>
            </div>

            <div className="finnish-requirement">
              <h3>100 MW Data Center Requirement</h3>
              <div className="finnish-req-row">
                <span>Land needed</span>
                <span>15 ha</span>
                <span className={totalArea >= 15 ? 'finnish-check' : 'finnish-x'}>
                  {totalArea >= 15 ? '✓' : '✗'}
                </span>
              </div>
              <div className="finnish-req-row">
                <span>Grid capacity</span>
                <span>100 MW</span>
                <span className={SEINAJOKI_STATION.free_mw >= 100 ? 'finnish-check' : 'finnish-x'}>
                  {SEINAJOKI_STATION.free_mw >= 100 ? '✓' : '✗'}
                </span>
              </div>
            </div>
          </div>

          {/* Vacant parcels list */}
          <div className="finnish-parcel-list">
            <h3>🏭 Vacant Parcels</h3>
            {VACANT_PARCELS.sort((a, b) => b.area_ha - a.area_ha).map((p) => (
              <div 
                key={p.parcel_id}
                className={`finnish-parcel-item finnish-vacant-${p.status}`}
                onClick={() => {
                  // Calculate centroid from polygon coordinates
                  const coords = p.geometry.coordinates[0];
                  const center = coords.reduce((acc, coord) => [acc[0] + coord[0]/coords.length, acc[1] + coord[1]/coords.length], [0, 0]);
                  copyToClipboard(center[1], center[0]);
                  mapRef.current?.flyTo({ center, zoom: 14 });
                }}
              >
                <span className={`finnish-status-badge finnish-status-${p.status}`}>
                  {p.status === 'best' ? '★' : '●'}
                </span>
                <span className="finnish-parcel-kortteli">{p.parcel_id.slice(-6)}</span>
                <span className="finnish-parcel-pop">{p.area_ha} ha</span>
                <span className="finnish-parcel-area">{p.buildings} bldg</span>
                <span className="finnish-parcel-dist">{p.dist_km} km</span>
              </div>
            ))}
            
            <h3 style={{marginTop: '16px'}}>🌾 Need Rezoning</h3>
            {POTENTIAL_AREAS.map((v) => (
              <div 
                key={v.grid_id}
                className="finnish-parcel-item finnish-vacant-needs_rezoning"
                onClick={() => {
                  const center = v.geometry.reduce((acc, coord) => [acc[0] + coord[0]/v.geometry.length, acc[1] + coord[1]/v.geometry.length], [0, 0]);
                  mapRef.current?.flyTo({ center, zoom: 14 });
                }}
              >
                <span className="finnish-status-badge finnish-status-needs_rezoning">○</span>
                <span className="finnish-parcel-kortteli">{v.grid_id.slice(-7)}</span>
                <span className="finnish-parcel-pop">{v.zoning.slice(0, 12)}</span>
                <span className="finnish-parcel-area">Pop: {v.population}</span>
                <span className="finnish-parcel-dist">{v.dist_km} km</span>
              </div>
            ))}
          </div>
        </aside>

        {/* Map */}
        <div className="finnish-map-container">
          <Map
            ref={mapRef}
            {...viewState}
            onMove={evt => setViewState(evt.viewState)}
            onClick={handleMapClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            interactiveLayerIds={['parcels-layer', 'vacant-parcels-fill']}
            mapStyle={MAP_STYLES[mapStyle]}
            mapboxAccessToken={MAPBOX_TOKEN}
            style={{ width: '100%', height: '100%' }}
          >
            <NavigationControl position="top-right" />

            {/* Municipality boundary */}
            <Source id="municipality" type="geojson" data={SEINAJOKI_BOUNDARY}>
              <Layer
                id="municipality-fill"
                type="fill"
                paint={{
                  'fill-color': '#3b82f6',
                  'fill-opacity': 0.05,
                }}
              />
              <Layer
                id="municipality-line"
                type="line"
                paint={{
                  'line-color': '#3b82f6',
                  'line-width': 2,
                  'line-dasharray': [4, 2],
                }}
              />
            </Source>

            {/* 6km radius around station */}
            <Source id="station-radius" type="geojson" data={stationRadiusGeoJSON}>
              <Layer
                id="station-radius-fill"
                type="fill"
                paint={{
                  'fill-color': '#f59e0b',
                  'fill-opacity': 0.08,
                }}
              />
              <Layer
                id="station-radius-line"
                type="line"
                paint={{
                  'line-color': '#f59e0b',
                  'line-width': 2,
                  'line-dasharray': [2, 2],
                }}
              />
            </Source>

            {/* Station */}
            <Source id="station" type="geojson" data={stationGeoJSON}>
              <Layer
                id="station-layer"
                type="circle"
                paint={{
                  'circle-radius': 12,
                  'circle-color': '#f59e0b',
                  'circle-stroke-width': 3,
                  'circle-stroke-color': '#ffffff',
                }}
              />
              <Layer
                id="station-label"
                type="symbol"
                layout={{
                  'text-field': ['get', 'name'],
                  'text-size': 12,
                  'text-offset': [0, 1.5],
                  'text-anchor': 'top',
                }}
                paint={{
                  'text-color': '#78350f',
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 2,
                }}
              />
            </Source>

            {/* Potential Areas - need rezoning (yellow) */}
            <Source id="potential-areas" type="geojson" data={potentialAreasGeoJSON}>
              <Layer
                id="potential-areas-fill"
                type="fill"
                paint={{
                  'fill-color': '#fbbf24',
                  'fill-opacity': 0.35,
                }}
              />
              <Layer
                id="potential-areas-line"
                type="line"
                paint={{
                  'line-color': '#d97706',
                  'line-width': 2,
                  'line-dasharray': [4, 2],
                }}
              />
              <Layer
                id="potential-areas-label"
                type="symbol"
                layout={{
                  'text-field': ['get', 'zoning'],
                  'text-size': 10,
                  'text-anchor': 'center',
                }}
                paint={{
                  'text-color': '#92400e',
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 1.5,
                }}
              />
            </Source>

            {/* Vacant Parcels - actual cadastral boundaries (green) */}
            <Source id="vacant-parcels" type="geojson" data={vacantParcelsGeoJSON}>
              <Layer
                id="vacant-parcels-fill"
                type="fill"
                paint={{
                  'fill-color': [
                    'match', ['get', 'status'],
                    'best', '#22c55e',
                    'good', '#4ade80',
                    '#86efac',
                  ],
                  'fill-opacity': 0.5,
                }}
              />
              <Layer
                id="vacant-parcels-line"
                type="line"
                paint={{
                  'line-color': [
                    'match', ['get', 'status'],
                    'best', '#15803d',
                    'good', '#16a34a',
                    '#22c55e',
                  ],
                  'line-width': 3,
                }}
              />
              <Layer
                id="vacant-parcels-label"
                type="symbol"
                layout={{
                  'text-field': [
                    'concat',
                    ['get', 'area_ha'], ' ha'
                  ],
                  'text-size': 11,
                  'text-anchor': 'center',
                }}
                paint={{
                  'text-color': '#166534',
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 2,
                }}
              />
            </Source>

            {/* Zoning parcels - reference only, smaller */}
            <Source id="parcels" type="geojson" data={parcelsGeoJSON}>
              <Layer
                id="parcels-layer"
                type="circle"
                paint={{
                  'circle-radius': 6,
                  'circle-color': '#94a3b8',
                  'circle-stroke-width': 1,
                  'circle-stroke-color': '#ffffff',
                  'circle-opacity': 0.6,
                }}
              />
            </Source>
          </Map>

          {/* Map style toggle */}
          <div className="finnish-map-toggle">
            <button 
              className={mapStyle === 'satellite' ? 'active' : ''}
              onClick={() => setMapStyle('satellite')}
            >
              🛰️ Satellite
            </button>
            <button 
              className={mapStyle === 'light' ? 'active' : ''}
              onClick={() => setMapStyle('light')}
            >
              🗺️ Map
            </button>
          </div>

          {/* Map legend */}
          <div className="finnish-legend">
            <h4>Vacant Parcels</h4>
            <div className="finnish-legend-item">
              <span className="finnish-legend-box" style={{ background: '#22c55e', border: '2px solid #15803d' }}></span>
              <span>Best (0-1 buildings)</span>
            </div>
            <div className="finnish-legend-item">
              <span className="finnish-legend-box" style={{ background: '#4ade80', border: '2px solid #16a34a' }}></span>
              <span>Good (no buildings)</span>
            </div>
            <div className="finnish-legend-item">
              <span className="finnish-legend-box" style={{ background: '#fbbf24', border: '2px dashed #d97706' }}></span>
              <span>Needs rezoning</span>
            </div>
            <div className="finnish-legend-item">
              <span className="finnish-legend-dot" style={{ background: '#f59e0b' }}></span>
              <span>110 kV Substation</span>
            </div>
            <p className="finnish-legend-note" style={{ color: '#166534' }}>
              ✓ MML Cadastre + CORINE Zoning
            </p>
          </div>
        </div>
      </div>

      {/* Copy toast message */}
      {copyMessage && (
        <div className="finnish-copy-toast">
          {copyMessage}
        </div>
      )}
    </div>
  );
}
